import React, { useState, useEffect, useCallback } from 'react';
import { X, Smartphone, Check, Loader } from 'lucide-react';

// Admin: publish grading periods to the parent app and push the report-card
// PDFs (bulletin, and carnet for CP–CM2) for one class. Only PUBLISHED periods
// are included in the PDFs, so mid-term marks never leak. Blocked families are
// skipped server-side.
const SendToAppModal = ({ apiCall, year, className, onClose, showSuccess, showError }) => {
  const [periods, setPeriods] = useState([]);
  const [types, setTypes] = useState({ bulletin: true, carnet: false });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');

  const loadState = useCallback(async () => {
    try {
      const data = await apiCall(
        `/academic/reports/publish-state?academicYear=${year}&className=${encodeURIComponent(className)}`);
      setPeriods(data);
    } catch (err) { showError(err.message); }
  }, [apiCall, year, className, showError]);

  useEffect(() => { loadState(); }, [loadState]);

  const togglePublish = async (p) => {
    try {
      const r = await apiCall('/academic/reports/publish', {
        method: 'POST',
        body: JSON.stringify({ periodId: p.PeriodID, published: !p.PublishedToMobile })
      });
      showSuccess(r.message);
      loadState();
    } catch (err) { showError(err.message); }
  };

  const anyPublished = periods.some(p => p.PublishedToMobile);
  const chosenTypes = Object.keys(types).filter(k => types[k]);

  const push = async () => {
    if (!anyPublished) { showError('Publish at least one period first'); return; }
    if (!chosenTypes.length) { showError('Choose at least one report to send'); return; }
    setBusy(true);
    try {
      let offset = 0, total = 0, pushed = 0, skipped = 0;
      do {
        const r = await apiCall('/academic/reports/push-report-cards-chunk', {
          method: 'POST',
          body: JSON.stringify({ academicYear: year, className, types: chosenTypes, offset, limit: 6 })
        });
        total = r.total; pushed += r.pushed; skipped += r.skipped;
        offset = r.nextOffset;
        setProgress(`${Math.min(offset, total)}/${total} students…`);
        if (!r.processed) break;
      } while (offset < total);
      showSuccess(`Report cards pushed: ${pushed} PDF(s)${skipped ? `, ${skipped} student(s) skipped (blocked)` : ''}`);
    } catch (err) { showError(err.message); }
    finally { setBusy(false); setProgress(''); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <Smartphone size={18} /> Send report cards to app — {className} · {year}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Publish toggles */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Published periods</p>
            <p className="text-xs text-gray-500 mb-2">
              Only published periods appear in the PDF, so mid-term marks stay hidden until you publish.
            </p>
            <div className="space-y-1.5">
              {periods.map(p => (
                <button
                  key={p.PeriodID}
                  onClick={() => togglePublish(p)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border text-sm ${
                    p.PublishedToMobile ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-gray-50 border-gray-200 text-gray-600'
                  }`}
                >
                  <span>{p.PeriodName}</span>
                  <span className="flex items-center gap-1 text-xs font-medium">
                    {p.PublishedToMobile ? <><Check size={14} /> Published</> : 'Hidden — tap to publish'}
                  </span>
                </button>
              ))}
              {periods.length === 0 && <p className="text-sm text-gray-400">No grading periods for this class.</p>}
            </div>
          </div>

          {/* Report types */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Reports to send</p>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={types.bulletin}
                  onChange={e => setTypes(t => ({ ...t, bulletin: e.target.checked }))} />
                Bulletin (numeric)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={types.carnet}
                  onChange={e => setTypes(t => ({ ...t, carnet: e.target.checked }))} />
                Carnet de compétences
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              The carnet is only generated for classes with competence objectives (CP–CM2); it's skipped otherwise.
            </p>
          </div>
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
          {busy && <span className="text-xs text-gray-500 flex items-center gap-1"><Loader size={13} className="animate-spin" /> {progress}</span>}
          <button onClick={onClose} className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Close</button>
          <button
            onClick={push} disabled={busy || !anyPublished}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
          >
            <Smartphone size={15} /> {busy ? 'Pushing…' : 'Generate & push'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendToAppModal;
